import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { User, Package, Calendar, Edit3, Save, X, ChevronDown, ChevronUp, Receipt, Clock, CheckCircle, Camera, Star, FileText } from 'lucide-react';
import axios from 'axios';
import { API, apiAssetUrl } from '@/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { getLocaleFromLang, pickLocalizedText } from '../utils/localizedContent';


export default function Profile() {
  const { user, updateUser } = useAuth();
  const { t, lang } = useLang();
  const [tab, setTab] = useState('profile');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', last_name: '', username: '', bio: '' });
  const [orders, setOrders] = useState([]);
  const [gameReservations, setGameReservations] = useState([]);
  const [tableReservations, setTableReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  const text = (es, en) => pickLocalizedText(lang, es, en);

  useEffect(() => {
    if (user) { setForm({ name: user.name || '', last_name: user.last_name || '', username: user.username || '', bio: user.bio || '' }); }
    axios.get(`${API}/payments/orders/my`).then(r => setOrders(r.data || [])).catch(() => {});
    axios.get(`${API}/reservations/my`).then(r => setGameReservations(r.data || [])).catch(() => {});
    axios.get(`${API}/tables/my`).then(r => setTableReservations(r.data || [])).catch(() => {});
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const { data } = await axios.put(`${API}/auth/profile`, form);
      updateUser(data);
      setEditing(false);
      setSaveMsg(text('¡Guardado!', 'Saved!'));
      setTimeout(() => setSaveMsg(''), 2000);
    } catch (err) {
      console.error('Failed to update profile:', err);
    }
    setLoading(false);
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result;
        const { data } = await axios.post(`${API}/auth/profile/avatar`, { image: base64 });
        updateUser({ avatar: data.avatar_url });
        setSaveMsg(text('¡Foto actualizada!', 'Photo updated!'));
        setTimeout(() => setSaveMsg(''), 3000);
        setUploadingAvatar(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Avatar upload failed:', err);
      setUploadingAvatar(false);
    }
  };

  if (!user) return null;

  const tabs = [
    { key: 'profile', label: t('profile.title'), icon: User },
    { key: 'orders', label: t('profile.orders'), icon: Package },
    { key: 'reservations', label: t('profile.reservations'), icon: Calendar },
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 page-enter">
      {/* Header */}
      <div className="flex items-center gap-6 mb-8">
        {/* Avatar with upload */}
        <div className="relative group">
          <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-[#0D5E4A] to-[#C9A962] flex items-center justify-center text-3xl font-black text-[#F5F0E6]">
            {user.avatar ? (
              <img
                src={apiAssetUrl(user.avatar)}
                alt={user.name}
                className="w-full h-full object-cover"
              />
            ) : (
              user.name?.[0]?.toUpperCase() || 'U'
            )}
          </div>
          {/* Camera button overlay */}
          <button
            onClick={() => avatarInputRef.current?.click()}
            disabled={uploadingAvatar}
            className="absolute inset-0 bg-black/60 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            title={text('Cambiar foto', 'Change photo')}
          >
            {uploadingAvatar ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Camera size={22} className="text-white" />
            )}
          </button>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleAvatarChange}
          />
        </div>
        <div>
          <h1 className="text-3xl font-black text-foreground" style={{ fontFamily: 'Cinzel, serif' }}>{user.name}</h1>
          {user.username ? <p className="text-[#C9A962] text-sm">@{user.username}</p> : null}
          <p className="text-muted-foreground text-sm">{user.email}</p>
          <span className={`mt-1 inline-block px-3 py-0.5 rounded-full text-xs font-semibold ${user.role === 'admin' ? 'bg-[#C9A962]/20 text-[#C9A962] border border-[#C9A962]/30' : 'bg-[#2D8B70]/20 text-[#2D8B70] border border-[#2D8B70]/30'}`}>
            {user.role === 'admin' ? 'Admin' : text('Usuario', 'User')}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 border-b border-border pb-4">
        {tabs.map(tab_ => (
          <button key={tab_.key} onClick={() => setTab(tab_.key)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${tab === tab_.key ? 'text-primary-foreground bg-primary' : 'text-muted-foreground hover:text-foreground bg-card border border-border'}`}
            data-testid={`profile-tab-${tab_.key}`}>
            <tab_.icon size={16} />{tab_.label}
          </button>
        ))}
      </div>

      {/* Profile tab */}
      {tab === 'profile' && (
        <div className="bg-card border border-border rounded-2xl p-6 max-w-lg">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-foreground font-bold">{t('profile.edit')}</h2>
            {!editing ? (
              <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-sm text-[#C9A962] hover:text-[#D4B87A] transition-colors" data-testid="edit-profile-btn">
                <Edit3 size={14} />{t('profile.edit')}
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={handleSave} disabled={loading} className="flex items-center gap-1 text-sm text-[#2D8B70] hover:text-[#3DA883]" data-testid="save-profile-btn">
                  <Save size={14} />{t('profile.save')}
                </button>
                <button onClick={() => { setEditing(false); setForm({ name: user.name || '', last_name: user.last_name || '', username: user.username || '', bio: user.bio || '' }); }} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                  <X size={14} />{t('profile.cancel')}
                </button>
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-muted-foreground text-sm block mb-1">{t('auth.name')}</label>
              {editing ? (
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-[#0A1612] border border-[#C9A962]/20 rounded-xl px-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962]"
                  data-testid="edit-name" />
              ) : <p className="text-foreground">{user.name}</p>}
            </div>
            <div>
              <label className="text-muted-foreground text-sm block mb-1">{text('Apellidos', 'Last name')}</label>
              {editing ? (
                <input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                  className="w-full bg-[#0A1612] border border-[#C9A962]/20 rounded-xl px-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962]"
                  data-testid="edit-last-name" />
              ) : <p className="text-foreground">{user.last_name || '—'}</p>}
            </div>
            <div>
              <label className="text-muted-foreground text-sm block mb-1">{t('auth.email')}</label>
              <p className="text-foreground">{user.email}</p>
            </div>
            <div>
              <label className="text-muted-foreground text-sm block mb-1">{text('Usuario', 'Username')}</label>
              {editing ? (
                <input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  className="w-full bg-[#0A1612] border border-[#C9A962]/20 rounded-xl px-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962]"
                  placeholder={text('tu_usuario', 'your_username')}
                  data-testid="edit-username" />
              ) : <p className="text-foreground">{user.username || '—'}</p>}
            </div>
            <div>
              <label className="text-muted-foreground text-sm block mb-1">Bio</label>
              {editing ? (
                <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} rows={3}
                  placeholder={text('Cuéntanos sobre ti...', 'Tell us about yourself...')}
                  className="w-full bg-[#0A1612] border border-[#C9A962]/20 rounded-xl px-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962] resize-none placeholder:text-[#A8B5AE]/50"
                  data-testid="edit-bio" />
              ) : <p className="text-muted-foreground">{user.bio || text('Sin bio', 'No bio')}</p>}
            </div>
          </div>
          {saveMsg && <p className="text-[#2D8B70] text-sm mt-3">{saveMsg}</p>}
        </div>
      )}

      {/* Orders tab */}
      {tab === 'orders' && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Cinzel, serif' }}>
              {text('Historial de Pedidos', 'Order History')}
            </h2>
            <span className="text-muted-foreground text-sm">
              {orders.length} {text('pedidos', 'orders')}
            </span>
          </div>
          {orders.length === 0 ? (
            <div className="text-center py-16 bg-card border border-border rounded-2xl">
              <Package size={56} className="mx-auto mb-4 text-[#C9A962]/30" />
              <h3 className="text-foreground font-semibold mb-2">
                {text('No tienes pedidos aún', 'No orders yet')}
              </h3>
              <p className="text-muted-foreground text-sm">
                {text('Tus compras aparecerán aquí', 'Your purchases will appear here')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map(order => (
                <OrderCard key={order.order_id} order={order} lang={lang} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reservations tab */}
      {tab === 'reservations' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-[#F5F0E6] font-bold mb-4" style={{ fontFamily: 'Cinzel, serif' }}>{text('Juegos Reservados', 'Game Reservations')}</h3>
            {gameReservations.length === 0 ? (
              <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl p-6 text-center">
                <Calendar size={32} className="mx-auto mb-2 text-[#C9A962]/30" />
                <p className="text-[#A8B5AE] text-sm">{text('Sin reservas de juegos', 'No game reservations')}</p>
              </div>
            ) : gameReservations.map(r => (
              <div key={r.reservation_id} className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl p-4 mb-3" data-testid={`game-res-${r.reservation_id}`}>
                <div className="flex justify-between mb-1">
                  <span className="text-[#F5F0E6] font-semibold text-sm">{r.game_name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.status === 'confirmed' ? 'bg-[#2D8B70]/20 text-[#2D8B70] border border-[#2D8B70]/30' : 'bg-[#C45B4A]/20 text-[#F87171] border border-[#C45B4A]/30'}`}>
                    {r.status === 'confirmed' ? text('Confirmado', 'Confirmed') : r.status}
                  </span>
                </div>
                <p className="text-[#A8B5AE] text-xs">{r.date} · {r.time_slot} ({r.duration_hours}h) · {r.party_size} {text('personas', 'people')}</p>
              </div>
            ))}
          </div>
          <div>
            <h3 className="text-[#F5F0E6] font-bold mb-4" style={{ fontFamily: 'Cinzel, serif' }}>{text('Mesas Reservadas', 'Table Reservations')}</h3>
            {tableReservations.length === 0 ? (
              <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl p-6 text-center">
                <Calendar size={32} className="mx-auto mb-2 text-[#C9A962]/30" />
                <p className="text-[#A8B5AE] text-sm">{text('Sin reservas de mesas', 'No table reservations')}</p>
              </div>
            ) : tableReservations.map(r => (
              <div key={r.reservation_id} className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl p-4 mb-3" data-testid={`table-res-${r.reservation_id}`}>
                <div className="flex justify-between mb-1">
                  <span className="text-[#F5F0E6] font-semibold text-sm">{r.table_name || `${text('Mesa', 'Table')} #${r.table_number}`}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.status === 'confirmed' ? 'bg-[#2D8B70]/20 text-[#2D8B70] border border-[#2D8B70]/30' : 'bg-[#C45B4A]/20 text-[#F87171] border border-[#C45B4A]/30'}`}>
                    {r.status === 'confirmed' ? text('Confirmado', 'Confirmed') : r.status}
                  </span>
                </div>
                <p className="text-[#A8B5AE] text-xs">{r.date} · {r.time_slot} ({r.duration_hours}h) · {r.party_size} {text('personas', 'people')}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function publicOrderLabel(order) {
  if (order.order_alias) return order.order_alias;
  const raw = (order.order_id || '').replace(/^ord_/i, '');
  const digits = raw.replace(/\D/g, '');
  if (digits.length >= 6) return digits.slice(-12);
  return order.order_id || '—';
}

// Componente de pedido con recibo desplegable
function OrderCard({ order, lang }) {
  const displayOrderId = publicOrderLabel(order);
  const [expanded, setExpanded] = useState(false);
  const text = (es, en) => pickLocalizedText(lang, es, en);
  
  const statusColors = {
    paid: 'bg-[#2D8B70]/20 text-[#2D8B70] border-[#2D8B70]/30',
    pending: 'bg-[#C9A962]/20 text-[#C9A962] border-[#C9A962]/30',
    cancelled: 'bg-[#C45B4A]/20 text-[#F87171] border-[#C45B4A]/30',
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(getLocaleFromLang(lang), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-2xl overflow-hidden transition-all" data-testid={`order-${order.order_id}`}>
      {/* Order Header - Always visible */}
      <button 
        onClick={() => setExpanded(!expanded)} 
        className="w-full p-5 flex items-center justify-between hover:bg-[#162B24]/50 transition-colors"
        data-testid={`order-toggle-${order.order_id}`}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#C9A962]/10 flex items-center justify-center">
            <Receipt size={20} className="text-[#C9A962]" />
          </div>
          <div className="text-left">
            <p className="text-[#F5F0E6] font-semibold text-sm">{displayOrderId}</p>
            <p className="text-[#A8B5AE] text-xs flex items-center gap-1">
              <Clock size={12} />
              {formatDate(order.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-[#C9A962] font-bold text-lg">{order.total?.toFixed(2)}€</p>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${statusColors[order.status] || statusColors.paid}`}>
              <CheckCircle size={10} />
              {order.status === 'paid' ? text('Pagado', 'Paid') : order.status}
            </span>
          </div>
          {expanded ? <ChevronUp size={20} className="text-[#A8B5AE]" /> : <ChevronDown size={20} className="text-[#A8B5AE]" />}
        </div>
      </button>
      
      {/* Expanded Receipt */}
      {expanded && (
        <div className="border-t border-[#C9A962]/10 p-5 bg-[#162B24]/30">
          {/* Items Table */}
          <div className="mb-4">
            <h4 className="text-[#C9A962] text-xs font-semibold uppercase tracking-wider mb-3">
              {text('Artículos', 'Items')}
            </h4>
            <div className="space-y-2">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-[#C9A962]/10 last:border-0">
                  <div className="w-14 h-14 rounded-lg bg-[#0A1612] border border-[#C9A962]/15 overflow-hidden flex-shrink-0">
                    {item.image_url ? (
                      <img src={apiAssetUrl(item.image_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#52525B] text-[10px]">—</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#F5F0E6] text-sm font-medium truncate">{item.name}</p>
                    <p className="text-[#A8B5AE] text-xs">{item.price?.toFixed(2)}€ × {item.quantity}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {item.product_id ? (
                      <Link
                        to={`/store/${item.product_id}#product-review`}
                        className="p-2 rounded-lg border border-[#C9A962]/25 text-[#C9A962] hover:bg-[#C9A962]/10 transition-colors"
                        title={text('Valorar producto', 'Rate this product')}
                        aria-label={text('Valorar producto', 'Rate this product')}
                      >
                        <Star size={18} className="shrink-0" />
                      </Link>
                    ) : null}
                    <p className="text-[#F5F0E6] font-semibold text-sm whitespace-nowrap">{(item.price * item.quantity).toFixed(2)}€</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {order.stripe_invoice_url ? (
            <div className="mb-4">
              <a
                href={order.stripe_invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[#C9A962] hover:text-[#D4B87A] font-semibold border border-[#C9A962]/30 rounded-xl px-4 py-2 transition-colors"
              >
                <FileText size={16} />
                {text('Ver factura (Stripe)', 'View invoice (Stripe)')}
              </a>
            </div>
          ) : null}
          
          {/* Totals */}
          <div className="border-t border-[#C9A962]/20 pt-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-[#A8B5AE] text-sm">{text('Subtotal', 'Subtotal')}</span>
              <span className="text-[#F5F0E6]">{order.total?.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#F5F0E6] font-bold">{text('Total', 'Total')}</span>
              <span className="text-[#C9A962] font-bold text-xl">{order.total?.toFixed(2)}€</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
